import { Flex, Skeleton } from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import React from "react";
import { RemoteDataView } from "src/components/Layout/RemoteDataView";
import { AdminHeading } from "src/components/Text/AdminHeading";
import { getAdminGreeting } from "src/generated/hooks/form-service/requests";
import {
  useOrganization,
  UseOrganizationType,
} from "src/hooks/useOrganization";
import * as RD from "src/types/remoteData";

export const Greeting: React.FC = () => {
  const greetingRD = useGreeting();
  return (
    <RemoteDataView
      remoteData={greetingRD}
      loading={<Loading />}
      error={(error) => <DefaultGreeting error={error} />}
      config={{ showDataWhileReloading: false }}
    >
      {(greeting) => (
        <AdminHeading title={greeting.title} subtitle={greeting.subtitle} />
      )}
    </RemoteDataView>
  );
};

const DefaultGreeting = ({ error }: { error: unknown }) => {
  React.useEffect(() => {
    console.error(error);
  }, [error]);
  return <AdminHeading title="Hi" subtitle="Ready to make things happen?" />;
};

export const useGreeting = (): RD.RemoteData<
  unknown,
  { title: string; subtitle: string }
> => {
  const organizationRD: UseOrganizationType = useOrganization();

  const query = useQuery({
    queryKey: ["admin-greeting", organizationRD.toNullable()?.id],
    queryFn: () => {
      const organizationId = organizationRD.toNullable()?.id;
      if (!organizationId) throw new Error("Organization ID not available");

      return getAdminGreeting({
        path: { organizationId },
      }).then((response) => response.data?.data);
    },
    enabled: organizationRD.hasData(),
  });

  const greetingRD = React.useMemo(() => RD.fromRestResult(query), [query]);

  if (organizationRD.isLoading()) {
    return RD.loading();
  }

  return greetingRD;
};

const Loading = () => (
  <Flex direction="column" gap={2} w="100%">
    <Flex direction="column" gap={2}>
      <Skeleton height="1.875rem" width="240px" borderRadius="md" />
      <Skeleton height="1.125rem" width="320px" borderRadius="md" />
    </Flex>
  </Flex>
);
